import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { AlertTriangle, ArrowRight, Eye, Sparkles } from 'lucide-react';
import ReferralPrompt from '../components/ReferralPrompt';

type ShiftStep = 'intro' | 'reveal-1' | 'reveal-2' | 'reveal-3' | 'reframe';

// The AI-generated text with embedded problems
const AI_TEXT = `Research suggests that implementing a structured morning routine can increase productivity by up to 47%. Many experts believe that the first two hours after waking represent a critical window for cognitive performance. Studies have shown that individuals who establish consistent wake times experience 23% better focus throughout their workday. The key is to avoid decision fatigue by planning your morning the night before, which approximately 78% of high performers reportedly do.`;

// Highlight definitions for each problem
const HIGHLIGHTS = {
  'reveal-1': {
    phrases: ['Research suggests', 'Many experts believe', 'Studies have shown'],
    title: 'Confident Hedging',
    explanation: 'These phrases sound authoritative but commit to nothing. Which research? Which experts? Which studies? This is how AI creates an illusion of credibility without any actual sources.',
    color: 'bg-yellow-500/30 border-yellow-500'
  },
  'reveal-2': {
    phrases: ['critical window for cognitive performance'],
    title: 'Unsupported Claim',
    explanation: 'This sounds scientific but is stated as fact with zero evidence. There\'s no citation, no study name, no researcher. AI often presents speculation as established science.',
    color: 'bg-orange-500/30 border-orange-500'
  },
  'reveal-3': {
    phrases: ['up to 47%', '23% better focus', 'approximately 78%'],
    title: 'False Precision',
    explanation: 'Specific numbers feel trustworthy. But where did "47%" come from? AI frequently fabricates statistics because precise numbers are persuasive, even when completely made up.',
    color: 'bg-red-500/30 border-red-500'
  }
};

function generateSessionId() {
  return 'shift_' + Math.random().toString(36).substring(2, 15);
}

export default function Shift() {
  const [step, setStep] = useState<ShiftStep>('intro');
  const [startTime] = useState(Date.now());
  const [sessionId] = useState(generateSessionId);
  const [searchParams] = useSearchParams();
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchApi } = useApi();

  const source = searchParams.get('from') || 'direct';

  // Track shift started on mount
  useEffect(() => {
    trackEvent('shift_started', { source });
  }, []);

  // Track step views
  useEffect(() => {
    if (step !== 'intro') {
      const stepNumber = step === 'reframe' ? 5 : parseInt(step.split('-')[1]) + 1;
      trackEvent('shift_step_viewed', { step: stepNumber });
    }
  }, [step]);

  async function trackEvent(eventType: string, data: Record<string, unknown> = {}) {
    try {
      await fetchApi('/api/shift/event', {
        method: 'POST',
        body: JSON.stringify({
          event_type: eventType,
          session_id: sessionId,
          ...data
        })
      });
    } catch (e) {
      console.error('Failed to track event:', e);
    }
  }

  async function handleComplete() {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);

    try {
      await fetchApi('/api/shift/complete', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          time_spent_seconds: timeSpent,
          source
        })
      });
    } catch (e) {
      console.error('Failed to record completion:', e);
    }

    setStep('reframe');

    // Show referral prompt for logged-in users after a brief delay
    if (user) {
      setTimeout(() => setShowReferralPrompt(true), 2000);
    }
  }

  function handleCTA() {
    trackEvent('shift_cta_clicked', {
      destination: user ? 'dashboard' : 'register'
    });

    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register?from=shift');
    }
  }

  function renderHighlightedText(highlightKey: keyof typeof HIGHLIGHTS | null) {
    if (!highlightKey) {
      return <p className="text-lg text-gray-200 leading-relaxed">{AI_TEXT}</p>;
    }

    const { phrases, color } = HIGHLIGHTS[highlightKey];
    let text = AI_TEXT;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;

    // Find all phrase positions and sort by index
    const matches: { phrase: string; index: number }[] = [];
    phrases.forEach(phrase => {
      let idx = text.indexOf(phrase);
      while (idx !== -1) {
        matches.push({ phrase, index: idx });
        idx = text.indexOf(phrase, idx + 1);
      }
    });
    matches.sort((a, b) => a.index - b.index);

    // Build parts array
    matches.forEach(({ phrase, index }, i) => {
      if (index > lastIndex) {
        parts.push(text.slice(lastIndex, index));
      }
      parts.push(
        <span
          key={`highlight-${i}`}
          className={`${color} border-b-2 px-1 py-0.5 rounded transition-all duration-500`}
        >
          {phrase}
        </span>
      );
      lastIndex = index + phrase.length;
    });

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return <p className="text-lg text-gray-200 leading-relaxed">{parts}</p>;
  }

  // Progress indicator
  const steps: ShiftStep[] = ['intro', 'reveal-1', 'reveal-2', 'reveal-3', 'reframe'];
  const currentIndex = steps.indexOf(step);

  return (
    <div className="max-w-2xl mx-auto py-8">
      {/* Progress dots */}
      <div className="flex justify-center gap-2 mb-8">
        {steps.map((s, i) => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              i <= currentIndex ? 'bg-accent-blue' : 'bg-gray-700'
            }`}
          />
        ))}
      </div>

      {/* Intro screen */}
      {step === 'intro' && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 rounded-full text-amber-400 text-sm mb-4">
              <Eye className="w-4 h-4" />
              3-minute challenge
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Can You Spot What's Wrong?
            </h1>
            <p className="text-gray-400">
              This paragraph was written by AI. It looks convincing. But there are 3 hidden problems.
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            {renderHighlightedText(null)}
          </div>

          <div className="text-center">
            <p className="text-gray-500 mb-4">Ready to see what you missed?</p>
            <button
              onClick={() => setStep('reveal-1')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-blue-500 text-slate-900 font-medium rounded-lg transition-colors"
            >
              Show me
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reveal screens */}
      {(step === 'reveal-1' || step === 'reveal-2' || step === 'reveal-3') && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 rounded-full text-amber-400 text-sm mb-4">
              <AlertTriangle className="w-4 h-4" />
              Problem {step.split('-')[1]} of 3
            </div>
            <h2 className="text-2xl font-bold text-white">
              {HIGHLIGHTS[step].title}
            </h2>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            {renderHighlightedText(step)}
          </div>

          <div className={`${HIGHLIGHTS[step].color.split(' ')[0]} border ${HIGHLIGHTS[step].color.split(' ')[1]} rounded-xl p-4`}>
            <p className="text-gray-200">
              {HIGHLIGHTS[step].explanation}
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => {
                if (step === 'reveal-1') setStep('reveal-2');
                else if (step === 'reveal-2') setStep('reveal-3');
                else handleComplete();
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-blue-500 text-slate-900 font-medium rounded-lg transition-colors"
            >
              {step === 'reveal-3' ? 'See the big picture' : 'Next problem'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reframe screen */}
      {step === 'reframe' && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-500/10 rounded-full text-green-400 text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              The shift
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              You Just Learned 3 AI Deception Patterns
            </h1>
          </div>

          <div className="bg-gradient-to-r from-gray-800/50 to-gray-800/30 border border-gray-700 rounded-xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-yellow-400 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-white">Confident Hedging</h3>
                <p className="text-sm text-gray-400">"Studies suggest" without naming any studies</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-orange-400 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-white">Unsupported Claims</h3>
                <p className="text-sm text-gray-400">Scientific-sounding assertions with zero evidence</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-400 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-white">False Precision</h3>
                <p className="text-sm text-gray-400">Fabricated statistics that sound credible</p>
              </div>
            </div>
          </div>

          <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-xl p-6 text-center">
            <p className="text-lg text-gray-200 mb-2">
              <strong className="text-white">Most people read AI output like they read human writing.</strong>
            </p>
            <p className="text-gray-400">
              That's the problem. At Northcoast AI Academy, we train you to see through the confidence.
            </p>
          </div>

          <div className="text-center pt-4">
            <button
              onClick={handleCTA}
              className="inline-flex items-center gap-2 px-8 py-4 bg-accent-blue hover:bg-blue-500 text-slate-900 font-semibold rounded-lg transition-colors text-lg"
            >
              {user ? 'Continue learning' : 'Start learning free'}
              <ArrowRight className="w-5 h-5" />
            </button>
            {!user && (
              <p className="text-gray-500 text-sm mt-3">
                No credit card required
              </p>
            )}
          </div>
        </div>
      )}

      {/* Referral Prompt Modal */}
      {showReferralPrompt && (
        <ReferralPrompt
          trigger="shift"
          onClose={() => setShowReferralPrompt(false)}
        />
      )}
    </div>
  );
}
